import { parseEntityRef } from '@backstage/catalog-model';
import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';
import type {
  EntityScorecard,
  FactDefinition,
  PackageListResponse,
  ResultsQuery,
  ScorecardComponentsPage,
  ScorecardResultsPage,
  Selector,
  SelectorMatchesResponse,
  Template,
  TemplateInput,
  TemplatePreviewRequest,
  TemplatePreviewResponse,
} from '@internal/plugin-scorecards-common';

/**
 * The router rejects a free-text search (`q`) longer than this. Only `q` is
 * clamped; the other filters are exact matches and must not be truncated.
 */
const MAX_FILTER_LENGTH = 200;

export interface ScorecardsApi {
  listFacts(): Promise<FactDefinition[]>;
  listTemplates(): Promise<Template[]>;
  getTemplate(id: string): Promise<Template>;
  createTemplate(input: TemplateInput): Promise<Template>;
  /**
   * Replaces a template. `version` is the `version` of the template the edit
   * was based on (from `getTemplate` or the last save); the backend rejects
   * the write with a 412 (see `isTemplateVersionConflict`) when someone changed
   * the template since. The client
   * sends it as `If-Match: "<version>"`, which is the ETag the backend sets, so
   * it does not need to read any response header.
   */
  updateTemplate(
    id: string,
    input: TemplateInput,
    version: number,
  ): Promise<Template>;
  /**
   * `version` is optional: when given it is sent as `If-Match` and a
   * template that changed since is answered with a 412 instead of deleted.
   */
  deleteTemplate(id: string, version?: number): Promise<void>;
  previewTemplate(
    request: TemplatePreviewRequest,
  ): Promise<TemplatePreviewResponse>;
  matchSelectors(selectors: Selector[]): Promise<SelectorMatchesResponse>;
  /** One row per component and template (the flat grid). */
  queryResults(query: ResultsQuery): Promise<ScorecardResultsPage>;
  /**
   * Pages over components, each with its scorecards (the tree). `query.sort`
   * must be `title`, `owner` or `system`; `total` counts components.
   */
  queryComponentResults(query: ResultsQuery): Promise<ScorecardComponentsPage>;
  /**
   * Every scorecard of a component, lowest template priority number first;
   * empty when it has none. The backend answers 404 for "not evaluated yet",
   * "not scorable" and "no template matches", and a missing or absent plugin
   * also answers 404, so all of these read as an empty list and callers
   * cannot (and should not try to) tell them apart.
   */
  getEntityScorecards(entityRef: string): Promise<EntityScorecard[]>;
  /**
   * The packages behind one package fact (`packages.*`) of a component, from
   * what the backend stored at its last collections. Rejects with a
   * `ResponseError` for any failure, 404 included (unlike
   * `getEntityScorecards`): the caller only asks for components that have a
   * scorecard.
   */
  getPackageList(entityRef: string, fact: string): Promise<PackageListResponse>;
}

export const scorecardsApiRef = createApiRef<ScorecardsApi>({
  id: 'plugin.scorecards.service',
});

/**
 * True when `error` means the scorecards backend is not serving requests: 404
 * (plugin not installed) or 503 (it failed to start, which is non-fatal for
 * the rest of the backend). The UI should show an "unavailable" state rather
 * than a generic failure.
 *
 * Only meaningful for collection endpoints (`listFacts`, `listTemplates`,
 * `queryResults`); a 404 from `getTemplate`, `updateTemplate` or
 * `deleteTemplate` means the template does not exist.
 */
export function isScorecardsUnavailable(error: unknown): boolean {
  return (
    error instanceof ResponseError &&
    (error.response.status === 404 || error.response.status === 503)
  );
}

/**
 * True when `error` is the 412 Precondition Failed the backend answers when a
 * template changed since the `version` an update or delete was based on. A
 * template name clash is a different failure (409) and is not matched here.
 */
export function isTemplateVersionConflict(error: unknown): boolean {
  return error instanceof ResponseError && error.response.status === 412;
}

/** Digits only, as the router's `limit` and `offset` parsing requires. */
function integerParam(name: string, value: number): string {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new TypeError(`${name} must be a non-negative integer`);
  }
  return String(value);
}

/** The `?...` of a results request; only parameters the router parses. */
function resultsQueryString(
  query: ResultsQuery,
  groupBy?: 'component',
): string {
  // Built field by field so only parameters the router parses are sent,
  // each as a single string.
  const params = new URLSearchParams();
  const set = (name: string, value: string | undefined) => {
    if (value !== undefined && value !== '') {
      params.set(name, value);
    }
  };
  set('groupBy', groupBy);
  set(
    'offset',
    query.offset === undefined
      ? undefined
      : integerParam('offset', query.offset),
  );
  set(
    'limit',
    query.limit === undefined ? undefined : integerParam('limit', query.limit),
  );
  set('sort', query.sort);
  set('order', query.order);
  set('level', query.level);
  set('owner', query.owner);
  set('system', query.system);
  set('templateId', query.templateId);
  // Free text from a search box: clamp it rather than have the router
  // answer 400 for a long paste.
  set('q', query.q?.slice(0, MAX_FILTER_LENGTH));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/**
 * The precondition header for a template at `version`. The backend's ETag for
 * a template is its version as a quoted decimal, so it is built here from
 * `template.version` rather than read from a response header.
 */
function ifMatch(version: number): Record<string, string> {
  return { 'If-Match': `"${integerParam('version', version)}"` };
}

export class ScorecardsClient implements ScorecardsApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  /** Credentials are added by the injected `fetchApi`; none are handled here. */
  private async request(path: string, init: RequestInit = {}) {
    const baseUrl = await this.discoveryApi.getBaseUrl('scorecards');
    return this.fetchApi.fetch(`${baseUrl}${path}`, {
      ...init,
      ...(init.body === undefined
        ? {}
        : { headers: { 'Content-Type': 'application/json', ...init.headers } }),
    });
  }

  private async json<T>(path: string, init?: RequestInit): Promise<T> {
    const response = await this.request(path, init);
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    return (await response.json()) as T;
  }

  /** `kind/namespace/name`, lower-cased and encoded. Throws for an unparsable ref. */
  private entityPath(entityRef: string): string {
    const { kind, namespace, name } = parseEntityRef(entityRef, {
      defaultKind: 'Component',
      defaultNamespace: 'default',
    });
    return [kind, namespace, name]
      .map(part => encodeURIComponent(part.toLowerCase()))
      .join('/');
  }

  private send(method: string, data: unknown): RequestInit {
    return { method, body: JSON.stringify(data) };
  }

  listFacts() {
    return this.json<FactDefinition[]>('/facts');
  }

  listTemplates() {
    return this.json<Template[]>('/templates');
  }

  getTemplate(id: string) {
    return this.json<Template>(`/templates/${encodeURIComponent(id)}`);
  }

  createTemplate(input: TemplateInput) {
    return this.json<Template>('/templates', this.send('POST', input));
  }

  async updateTemplate(id: string, input: TemplateInput, version: number) {
    return this.json<Template>(`/templates/${encodeURIComponent(id)}`, {
      ...this.send('PUT', input),
      headers: ifMatch(version),
    });
  }

  async deleteTemplate(id: string, version?: number) {
    const response = await this.request(
      `/templates/${encodeURIComponent(id)}`,
      {
        method: 'DELETE',
        ...(version === undefined ? {} : { headers: ifMatch(version) }),
      },
    );
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
  }

  previewTemplate(request: TemplatePreviewRequest) {
    return this.json<TemplatePreviewResponse>(
      '/templates/preview',
      this.send('POST', request),
    );
  }

  matchSelectors(selectors: Selector[]) {
    return this.json<SelectorMatchesResponse>(
      '/templates/matches',
      this.send('POST', { selectors }),
    );
  }

  async queryResults(query: ResultsQuery) {
    return this.json<ScorecardResultsPage>(
      `/results${resultsQueryString(query)}`,
    );
  }

  async queryComponentResults(query: ResultsQuery) {
    return this.json<ScorecardComponentsPage>(
      `/results${resultsQueryString(query, 'component')}`,
    );
  }

  async getEntityScorecards(entityRef: string) {
    const response = await this.request(
      `/entities/${this.entityPath(entityRef)}`,
    );
    if (response.status === 404) {
      return [];
    }
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    return (await response.json()) as EntityScorecard[];
  }

  async getPackageList(entityRef: string, fact: string) {
    return this.json<PackageListResponse>(
      `/entities/${this.entityPath(
        entityRef,
      )}/packages?fact=${encodeURIComponent(fact)}`,
    );
  }
}
